import {
  canCloneDesign,
  Design,
  DesignLogo,
  DesignState,
  getDesignState,
} from "../../../../core/models/entities/Design";
import {
  BaseLogo,
  getProductPrice,
  getTypeText,
} from "../../../../core/models/entities/BaseLogo";
import { Switch } from "../../../components/ui/Switch";
import React, { forwardRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BoxHeader2 } from "../../../components/BoxView/BoxHeader2";
import { BoxHeaderTypography } from "../../../components/Typographies/BoxHeaderTypography";
import { StrucRow } from "../../../components/StructureElements/StrucRow";
import { StrucCol } from "../../../components/StructureElements/StrucCol";
import { formatDate } from "../../../../utils/functions/DateUtil";
import {
  SecondaryButton,
  SuccessButton,
  WarnButton,
} from "../../../components/Buttons/SecondaryButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faClone,
  faDownload,
  faFile,
  faForward,
  faList,
  faShare,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { Popover, Tooltip } from "@mui/material";
import { useDesignActionHandler } from "../hooks/useDesignActionHandler";
import IconButton from "../../../components/LogoButton/IconButton";
import LoadingElement from "../../../components/loading-element/loading-element";
import { LogoDimensions } from "../../../components/Logo/LogoDimensions";
import {
  ProductPendingBadge,
  ProductPendingLock,
} from "../../../components/Logo/ProductPendingBadge";
import {
  designProductPendingSinceUtc,
  designActionLockVariant,
  isDesignActionLocked,
  isDesignPricePending,
} from "../../../../utils/functions/productPending";
import { DesignStatusElement } from "../../../components/Design/DesignStatusElement";
import {
  PriceLine,
  getUniqueSortedPrices,
} from "../../../components/Logo/LogoPrices";
import { Role } from "../../../../core/models/entities/Role";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useCartHidden } from "../../../../hooks/useCartHidden";
import { performCavemanReFetch } from "../../../../utils/functions/caveman-utils";
import { BoxModalContent } from "../../../components/BoxView/BoxModalContent";
import { Stepper } from "../../../components/Buttons/Stepper";
import {
  getDesignMinQuantity,
  isValidDesignQuantity,
} from "../../../../utils/functions/QuantityUtil";
import {
  AddToCartIcon,
  CloneIcon,
  EditIcon,
  TrashcanCrossIcon,
} from "../../../../resources/icons/Icons";
import { CustomTextArea } from "../../../components/Input/CustomInput";
import { PostItNoteIcon } from "../../../../resources/icons/PostItNoteIcon";
import { RadioButton } from "../../../shared/components/RadioButton";
import { VStack } from "../../../shared/components/VStack";
import { isDesignApprovedByMe } from "./design-list-item.component";
import { getColorsElements } from "../../order-create/subcomponents/ProductDetailsLogo";
import { LogoTransferMethod } from "../../../components/Logo/LogoTransferMethod";
import { DesignLogoStorageLine } from "../../../components/Logo/DesignLogoStorageLine";
import { getTransferDisplayName } from "../../../../core/models/entities/BaseLogo";
import { LogoImage } from "../../../components/Logo/LogoImage";
import EmailDialog from "../../../components/Approval/EmailDialog";
import { ApprovalConfirmationDialog } from "../../../components/Dialogs/ApprovalConfirmationDialog";
import { ApprovalStatus } from "../../../../core/models/entities/Approval";
import { hasUserPendingApprovalStatus } from "../../../../core/models/entities/BaseLogo";
import { LogoPdfGenerator } from "../../../../utils/functions/pdf-generator.service";
import { useWhiteLabelContext } from "../../../../WhiteLabelContext";
import { useErrorToast } from "../../../components/Toast/Toast";
import { useApiClient } from "../../../../data/clients/ApiClient";
import { usePdfDownloadWithSharpGate } from "../../../../hooks/usePdfDownloadWithSharpGate";
import { useDesignRepository } from "../../../../data/repositories/DesignRepository";
import { enviroment } from "../../../../enviroment";
import { CLARITY_MASK } from "../../../../utils/analytics/clarityAttrs";
import { verdictLockState } from "./verdictLock";
import { DataError } from "../../../../core/errors/DataError";
import { usePriceVisibility } from "../../../../contexts/PriceVisibilityContext";
import { SalesPriceBadge } from "../../../components/Logo/SalesPriceBadge";
import { AssignCustomerDialog } from "../../../components/Dialogs/assign-customer-dialog";
import { useDialog } from "../../../components/Dialogs/dialog-base";
import { isPlaceholder } from "../../../../core/models/entities/BaseLogo";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { ConfirmationGuard } from "../../../components/ConfirmationGuard";
import { useTranslation } from "react-i18next";

const LogoList = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  return (
    <div
      ref={ref}
      className={"my-4 max-h-[710px] overflow-y-scroll overflow-x-hidden"}
    >
      <div className={"h-full mr-[-0.75rem]"}>
        <div className={"mr-[1rem] space-y-2 mb-20"}>{props.children}</div>
      </div>
    </div>
  );
});

export type DesignModalIntent = "view" | "reject" | "approve";
type RejectOption = "remark" | "delete" | "editAfterReject";

type ButtonAction =
  | "cancel"
  | "delete"
  | "edit"
  | "approve"
  | "clone"
  | "copy"
  | "reject"
  | "skip"
  | "space"
  | "add_to_order";

// "Reject and fix it myself" is offered to the dealer/admin only (the
// customer's reject dialog is untouched) and only when the design carries a
// style - the editor (DesignProvider) requires design.style and redirects or
// renders blank without it. Always false with the flag off.
export function canRejectAndEdit(design: Design, isCustomer: boolean): boolean {
  return enviroment.rejectAndEditEnabled && !isCustomer && !!design.style;
}

function resolveInitialRemark(design: Design) {
  return (
    design.approval?.customer?.rejectionNote ||
    design.approval?.external?.rejectionNote ||
    design.approval?.vendor?.rejectionNote ||
    ""
  );
}

function AssignCustomerButton({
  design,
  role,
  onClick,
}: {
  design: Design;
  role: Role;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const shouldShow = role !== Role.EndUser && design.customer?.type == "vendor";

  if (!shouldShow) return <></>;

  return (
    <IconButton
      key={"assign-customer"}
      onClick={onClick}
      icon={<FontAwesomeIcon icon={faUserPlus} />}
      variant="dim"
      className="w-full !h-9"
    >
      {t("designListStyle.assignCustomer")}
    </IconButton>
  );
}

type DefaultOnClick = () => void;
interface DefaultActionButtonProps {
  onClick: DefaultOnClick;
  disabled?: boolean;
}

export function DesignDetails({
  design,
  intent = "view",
  onClose,
  onShowRejectionNote,
  closeDialog,
  onDeleteDesign,
  onRejectAndDelete,
  onAssignCustomer,
}: {
  design: Design;
  intent?: DesignModalIntent;
  onShowRejectionNote: (design: Design) => void;
  onClose: () => void;
  closeDialog: () => void;
  onDeleteDesign?: (design: Design) => void;
  onRejectAndDelete?: (design: Design, note?: string) => void | Promise<void>;
  onAssignCustomer: (customerId: number) => Promise<void>;
}) {
  const {
    handleDeleteDesign: defaultHandleDeleteDesign,
    handleRejectAndDelete: defaultHandleRejectAndDelete,
    handlePlaceVerdict,
    handleCopyDesign,
    handleGoToUpdateDesign,
    handleApprovalSkip,
    handleSendApproval,
    handleSendApprovalWithEndCustomerPending,
    handleAddToCart,
    handleChangeTitle,
    handleRejectAndEdit,
    isLoading,
  } = useDesignActionHandler();
  const { t } = useTranslation();

  // Use provided handlers or fall back to defaults
  const handleDeleteDesign = onDeleteDesign || defaultHandleDeleteDesign;
  const handleRejectAndDelete =
    onRejectAndDelete || defaultHandleRejectAndDelete;

  const { role } = useProfileContext();
  const whiteLabel = useWhiteLabelContext();
  const apiClient = useApiClient();
  // Flag REACT_APP_PDF_WAIT_SHARP_ENABLED: bounded wait for sharp logo images
  // + a dialog instead of a silently blurred PDF. Flag off = a plain
  // withToasts around the generator (as before) and a null dialog.
  // adoptKey: the wrapper REMOUNTS this component when the summary lands
  // (key "list-stub" -> "summary") - a download started before that must
  // hand its stop dialog (and its in-flight guard) over to the new instance.
  const { downloadPdf, sharpGateDialog } = usePdfDownloadWithSharpGate({
    colorHex: whiteLabel.whiteLabel.colorHex,
    adoptKey: `design:${design?.id}`,
  });
  const { hidePrices, showCustomerPrices } = usePriceVisibility();
  const cartHidden = useCartHidden();
  const navigate = useNavigate();

  const minQuantity = getDesignMinQuantity(design);
  const [quantityForOrder, setQuantityForOrder] = useState<number>(minQuantity);
  const [rejectOption, setRejectOption] = useState<RejectOption>("remark");
  const [remark, setRemark] = useState(() => resolveInitialRemark(design));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [approvalEmail, setApprovalEmail] = useState(
    design?.customer?.email ?? "",
  );
  const [approvalConfirmationOpen, setApprovalConfirmationOpen] =
    useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  // Verdict lock (flag approvalPatchEnabled): true while an Approve/Reject
  // PUT is in flight - drives the overlay and disables the two buttons, so
  // the popup cannot fire a second verdict. With the flag off the overlay
  // follows isLoading as before and the buttons get no disabled attribute
  // (see verdictLockState).
  const [verdictPending, setVerdictPending] = useState(false);
  const verdictLock = verdictLockState({
    approvalPatchEnabled: enviroment.approvalPatchEnabled,
    isLoading,
    verdictPending,
  });
  const assignCustomerDialog = useDialog();
  // "Prices on their way" (flag productPendingEnabled): a placed logo's
  // product is still owed by the API worker - Approve / Skip / Send for
  // approval / Add to order are locked with a tooltip and the price column
  // shows the badge. Always false with the flag off.
  // "Preparing logo…" (flag cloneInstantEnabled): a placed clone still owed
  // its supplier identity locks the same buttons (upload tooltip) without
  // hiding the prices. Equals the price lock with that flag off.
  const pricePending = isDesignActionLocked(design);
  const lockVariant = designActionLockVariant(design);

  const isAdminOrVendor = role && [Role.Admin, Role.Vendor].includes(role);
  const cards = design.designLogos.map((dl) => (
    <LogoCard
      key={dl.inDesignId}
      designLogo={dl}
      isEditable={design.isEditable}
    />
  ));
  const designState = getDesignState(design);
  // Progressive image: paint the low-res the list already cached IMMEDIATELY,
  // then fade in the high-res once it has loaded. Previously the field showed
  // the high-res src directly (a fresh download / not-yet-cached URL), leaving
  // it blank while loading - the slow "t-shirt spinner" feel, and the image
  // that never appeared in Safari. The low-res is already downloaded (the list
  // row rendered it), so this costs no extra network.
  const lowResSrc = design.displayImage?.lowResUrl;
  const highResSrc = design.displayImage?.highResUrl;
  const baseImageSrc = lowResSrc ?? highResSrc;
  const showHighResOverlay = !!highResSrc && highResSrc !== baseImageSrc;
  const [highResLoaded, setHighResLoaded] = useState(false);

  if (role == null || role == undefined) {
    return <label>{t("designListStyle.roleNotSet")}</label>;
  }

  // Flag approvalPatchEnabled: run the verdict with the popup locked and
  // close it on success (on failure it stays open - withToasts has shown
  // the error). Flag off = today's fire-and-forget: close at once.
  function runLockedVerdict(action: () => Promise<unknown> | void) {
    setVerdictPending(true);
    Promise.resolve()
      .then(action)
      .then(() => onClose())
      .catch(() => {})
      .finally(() => setVerdictPending(false));
  }

  function onApprove() {
    if (enviroment.approvalPatchEnabled) {
      runLockedVerdict(() => handlePlaceVerdict(design, { approve: true }));
      return;
    }
    handlePlaceVerdict(design, { approve: true });
    onClose();
  }

  async function handleDownloadDesignPdf(design: Design) {
    try {
      await downloadPdf({
        run: (options) =>
          LogoPdfGenerator.generateDesignPdf(
            {
              design,
              vendorLogoUrl: whiteLabel.whiteLabel.logoUrl || undefined,
              apiClient,
            },
            options,
          ),
        loadingMessage: t("designListStyle.generatingDesignPdf"),
        successMessage: t("designListStyle.designPdfDownloaded"),
        errorMessage: t("designListStyle.designPdfFailed"),
      });
    } catch (error) {
      // Error is already handled by withToasts (inside downloadPdf)
      console.error("Failed to generate design PDF:", error);
    }
  }

  // Check if current user has pending approval
  const hasCurrentUserPendingApproval = () => {
    return hasUserPendingApprovalStatus(design.approval, role);
  };

  const startApprovalEmailFlow = () => {
    if (hasCurrentUserPendingApproval()) {
      setApprovalConfirmationOpen(true);
    } else {
      setDialogOpen(true);
    }
  };

  const handleApprovalConfirmation = () => {
    setApprovalConfirmationOpen(false);
    // Mark that approval is pending (don't approve yet)
    setPendingApproval(true);
    // Show email dialog
    setDialogOpen(true);
  };

  const endApprovalEmailFlow = () => {
    // If there's a pending approval, approve first before sending email
    if (pendingApproval) {
      handlePlaceVerdict(design, { approve: true });
      setPendingApproval(false);
    }
    // Use conditional approval for Customers to preserve existing approval statuses
    // Use regular approval for Vendors who have full control
    if (role === Role.EndUser) {
      handleSendApprovalWithEndCustomerPending(design, approvalEmail).then(
        performCavemanReFetch,
      );
    } else {
      handleSendApproval(design, approvalEmail);
    }
  };

  // "Reject and fix it myself" (flag rejectAndEditEnabled): the ONE verdict
  // PUT (editAfterReject:true), then straight to the editor of the SAME
  // design - the same route as Edit, no cache invalidation in between (the
  // repository landed the response in place). When the API answers that the
  // design could not be unlocked - editAfterRejectApplied === false (a
  // locked design without a style), or the response still carries
  // isEditable:false (an API that ignored editAfterReject: older API / API
  // flag off; the editor renders blank on such a design) - it is rejected as
  // today: toast "use Clone" and close, no navigation. A failed PUT keeps
  // the popup open (withToasts has shown the error). The popup is locked
  // meanwhile exactly as a plain verdict (verdictLockState; inert with
  // approvalPatchEnabled off), and the REJECT & FIX button itself is
  // disabled while the PUT is in flight (this branch only).
  function onRejectAndEdit(remark: string) {
    setVerdictPending(true);
    handleRejectAndEdit(design, remark)
      .then((rejected) => {
        if (
          rejected.editAfterRejectApplied === false ||
          rejected.isEditable === false
        ) {
          useErrorToast(t("designListStyle.rejectAndEditEditorFailed"));
          onClose();
          return;
        }
        handleGoToUpdateDesign(design);
      })
      .catch(() => {})
      .finally(() => setVerdictPending(false));
  }

  function onReject(deleteOnRejection: boolean, remark: string) {
    if (enviroment.approvalPatchEnabled) {
      runLockedVerdict(() =>
        deleteOnRejection
          ? handleRejectAndDelete(design, remark)
          : handlePlaceVerdict(design, { approve: false, rejectionNote: remark }),
      );
      return;
    }
    if (deleteOnRejection) {
      handleRejectAndDelete(design, remark);
    } else {
      handlePlaceVerdict(design, { approve: false, rejectionNote: remark });
    }
    onClose();
  }

  function getBottomActionsForIntent(
    intent: DesignModalIntent,
    role: Role,
  ): ButtonAction[] {
    const filterCart = (actions: ButtonAction[]): ButtonAction[] =>
      cartHidden ? actions.filter((a) => a !== "add_to_order") : actions;
    switch (intent) {
      case "view":
        const designSate = getDesignState(design);
        return filterCart(getBottomActionsForDesignState(designSate, role));
      case "reject":
        return ["cancel", "space", "reject"];
      case "approve":
        return ["cancel", "space", "approve"];
    }
  }

  function getBottomActionsForDesignState(
    designState: DesignState,
    role: Role,
  ): ButtonAction[] {
    function withoutEditIfNotEditable(actions: ButtonAction[]): ButtonAction[] {
      return design.isEditable === false
        ? actions.filter((a) => a !== "edit")
        : actions;
    }
    // Check if current user has pending approval status
    const userHasPendingApproval = hasUserPendingApprovalStatus(
      design.approval,
      role,
    );

    // If user has pending approval status, show approval actions regardless of design state
    if (userHasPendingApproval) {
      return ["cancel", "delete", "approve"];
    }

    // Check if design is committed (has been sent to production)
    const isCommitted = design.product?.mul != null;

    switch (designState) {
      case DesignState.Draft:
        return withoutEditIfNotEditable(["cancel", "delete", "edit"]);
      case DesignState.Done:
        return ["cancel", "delete", "add_to_order"];
      case DesignState.Approval:
        return [
          "cancel",
          "delete",
          isDesignApprovedByMe(design, role) ? "skip" : "approve",
        ];
      default: {
        const defaultActions: ButtonAction[] = ["cancel", "delete", "edit"];
        // Flag rejectAndEditEnabled: a REJECTED design keeps its Edit button
        // even when it has a MUL (the dealer fixes it on the same MUL and
        // re-approves); the row already shows Edit for it, so the two agree.
        // Pending designs with a MUL, and the isEditable gate, are as today.
        const editStaysForCommitted =
          enviroment.rejectAndEditEnabled &&
          designState === DesignState.Rejected;
        const actions =
          isCommitted && !editStaysForCommitted
            ? defaultActions.filter((action) => action !== "edit")
            : defaultActions;
        return withoutEditIfNotEditable(actions);
      }
    }
  }

  function ActionButton({
    action,
    onSendApproval: onSendApprovalProp,
  }: {
    action: ButtonAction;
    onSendApproval?: () => void;
  }) {
    switch (action) {
      case "cancel":
        return <ActionButtons.Cancel onClick={onClose} />;
      case "delete":
        return (
          <ActionButtons.Delete
            onClick={() => handleDeleteDesign(design)}
            designState={designState}
          />
        );
      case "edit":
        return (
          <ActionButtons.Edit onClick={() => handleGoToUpdateDesign(design)} />
        );
      case "approve":
        return (
          <ProductPendingLock locked={pricePending} variant={lockVariant}>
            <ActionButtons.Approve
              onClick={pricePending ? () => {} : onApprove}
              disabled={verdictLock.buttonsDisabled || pricePending || undefined}
            />
          </ProductPendingLock>
        );
      case "clone":
        return <ActionButtons.Clone onClick={handleClone} />;
      case "copy":
        return <ActionButtons.Copy onClick={handleClone} />;
      case "reject":
        return (
          <ActionButtons.Reject
            disabled={
              verdictLock.buttonsDisabled ||
              (rejectOption === "editAfterReject" && verdictPending) ||
              undefined
            }
            onClick={() => {
              // Validate that rejection note is provided when "remark" option is selected
              if (rejectOption !== "delete" && !remark.trim()) {
                useErrorToast(t("designListStyle.rejectionNoteRequired"));
                return;
              }

              switch (rejectOption) {
                case "remark":
                  onReject(false, remark);
                  break;
                case "delete":
                  onReject(true, remark);
                  break;
                case "editAfterReject":
                  onRejectAndEdit(remark);
                  break;
              }
            }}
          >
            {rejectOption === "delete"
              ? t("designListStyle.deleteUpper")
              : rejectOption === "editAfterReject"
                ? t("designListStyle.rejectAndEditUpper")
                : t("designListStyle.rejectUpper")}
          </ActionButtons.Reject>
        );
      case "skip":
        return isAdminOrVendor &&
          !hasUserPendingApprovalStatus(design.approval, role as number) ? (
          <ProductPendingLock locked={pricePending} variant={lockVariant}>
            <ActionButtons.Skip
              onClick={
                pricePending ? () => {} : () => handleApprovalSkip(design)
              }
              disabled={pricePending || undefined}
            />
          </ProductPendingLock>
        ) : (
          <ProductPendingLock locked={pricePending} variant={lockVariant}>
            <ActionButtons.SendApproval
              onClick={pricePending ? () => {} : onSendApprovalProp || (() => {})}
              disabled={pricePending || undefined}
            />
          </ProductPendingLock>
        );
      case "space":
        return <></>;
      case "add_to_order":
        return (
          <ProductPendingLock locked={pricePending} variant={lockVariant}>
            <ActionButtons.AddToOrder
              disabled={
                !isValidDesignQuantity(design, quantityForOrder) || pricePending
              }
              onClick={
                pricePending
                  ? () => {}
                  : () => handleAddToCart(design, quantityForOrder)
              }
              requiresConfirmation={design.designLogos.some(
                (dl) => dl.logo.isDeleted,
              )}
            />
          </ProductPendingLock>
        );
    }
  }

  function handleClone() {
    handleCopyDesign(design).then((d) => handleGoToUpdateDesign(d, undefined, { justCloned: true }));
  }

  function handleTitle(title: string) {
    handleChangeTitle(design.customerId, design.id, title);
  }

  const BottomButtonBar = ({ role }: { role: Role }) => {
    const actions = getBottomActionsForIntent(intent, role);

    return (
      <StrucRow className="gap-x-[17px]">
        {actions.map((action) => (
          <StrucCol key={`col-${action}`}>
            {
              <ActionButton
                key={`button-${action}`}
                action={action}
                onSendApproval={startApprovalEmailFlow}
              />
            }
          </StrucCol>
        ))}
      </StrucRow>
    );
  };

  return (
    <>
      {verdictLock.overlay && <LoadingElement transparent />}
      {sharpGateDialog}
      <BoxHeader2>
        <DesignTitleHeader design={design} />
      </BoxHeader2>
      <BoxModalContent className={"w-[1200px] min-h-0 pb-8"}>
        <div className="grid grid-cols-12 gap-x-[29px]">
          <div className="col-span-8 grid grid-flow-row gap-y-[17px]">
            <StrucRow>
              <div className={"relative h-full w-full"}>
                {baseImageSrc && (
                  <img
                    className={"m-auto object-contain h-90 xl:h-128"}
                    src={baseImageSrc}
                    alt={design.title}
                  />
                )}
                {showHighResOverlay && (
                  <img
                    className={`m-auto object-contain h-90 xl:h-128 absolute inset-0 transition-opacity duration-300 ${
                      highResLoaded ? "opacity-100" : "opacity-0"
                    }`}
                    src={highResSrc}
                    alt={design.title}
                    onLoad={() => setHighResLoaded(true)}
                  />
                )}
              </div>
            </StrucRow>
            <div className="grid grid-cols-3 gap-x-[17px]">
              <DesignInfo design={design} onTitleChange={handleTitle} />
              {/* The design note no longer lives in this column — it moved to
                  the post-it in the popup's top-right corner (DesignNotePostIt,
                  flag designNoteEnabled), so the price column is back to its
                  pre-note markup regardless of the flag. */}
              {!hidePrices ? <DesignPrice design={design} /> : <div></div>}
              <StatusColumn
                role={role}
                design={design}
                intent={intent}
                designState={designState}
                remark={remark}
                rejectOption={rejectOption}
                setRejectOption={setRejectOption}
                setRemark={setRemark}
                quantity={quantityForOrder}
                setQuantity={(value) => setQuantityForOrder(value)}
                onClone={handleClone}
                onSendApproval={startApprovalEmailFlow}
                onDownloadPdf={handleDownloadDesignPdf}
                onAssignCustomerClick={assignCustomerDialog.openDialog}
              />
            </div>
            <BottomButtonBar role={role} />
            <EmailDialog
              open={dialogOpen}
              setOpen={setDialogOpen}
              email={approvalEmail}
              onEmailChange={setApprovalEmail}
              onSubmit={endApprovalEmailFlow}
            />
            <ApprovalConfirmationDialog
              open={approvalConfirmationOpen}
              onClose={() => setApprovalConfirmationOpen(false)}
              onApprove={handleApprovalConfirmation}
              logoTitle={design.title}
            />
            <AssignCustomerDialog
              dialogOption={assignCustomerDialog}
              onConfirm={async (customerId) => {
                await onAssignCustomer(customerId);
                assignCustomerDialog.closeDialog();
              }}
              isLoading={isLoading}
            />
          </div>
          <div className="col-span-4 flex flex-col relative">
            {/* Post-it note (flag designNoteEnabled): pinned in the popup's
                top-right corner — at/under the close-X, above "Logos in
                Design" (where Nichlas' arrow pointed). Absolutely positioned
                so the column layout is untouched, and deliberately NOT gated
                on hidePrices: a note is not a price. */}
            {enviroment.designNoteEnabled && <DesignNotePostIt design={design} />}
            <p className={"font-bold"}>{t("designListStyle.logosInDesign")}</p>
            {cards?.length > 0 ? (
              <LogoList>{cards}</LogoList>
            ) : (
              t("designListStyle.noLogos")
            )}
            <div className="mt-auto pt-2">
              <IconButton
                onClick={() =>
                  navigate(
                    `/logo?pageSize=25&pageIndex=1&filterBy=designlogos.designid&filterOn=${design.id}`
                  )
                }
                icon={<FontAwesomeIcon icon={faList} />}
                className="w-full !h-9"
              >
                {t("designListStyle.viewLogos")}
              </IconButton>
            </div>
          </div>
        </div>
      </BoxModalContent>
    </>
  );
}

function LogoCard({
  designLogo,
  isEditable,
}: {
  designLogo: DesignLogo;
  isEditable?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const logo = designLogo.logo;
  const isDeleted = logo.isDeleted;

  const colors = getColorsElements(logo);

  return (
    <div
      className={
        isDeleted
          ? "border border-red-500 rounded bg-gray-100 shadow-[0_0_8px_rgba(239,68,68,0.55)]"
          : "border rounded bg-gray-100"
      }
    >
      <StrucRow
        className={
          isDeleted ? "pl-1 pr-2 py-1" : "pl-1 pr-2 py-1 border-b"
        }
      >
        <StrucCol className={"!justify-between !flex-row items-center"}>
          <div className={"text-[#1B76DF] flex items-center space-x-2"}>
            {isDeleted && (
              <Tooltip
                title={t("designListStyle.deletedLogoTooltip")}
                placement="top"
                slotProps={{
                  tooltip: { sx: { fontSize: "0.8rem", maxWidth: 320 } },
                }}
              >
                <FontAwesomeIcon
                  className={"text-red-600 text-sm"}
                  icon={faTriangleExclamation}
                />
              </Tooltip>
            )}
            <FontAwesomeIcon
              className={"text-xs px-1.5 py-1 rounded border"}
              icon={faFile}
            />
            {logo.product?.sku && !logo.product.sku.startsWith("UPLOAD_") ? (
              <a
                href={`/logo?searchTerm=${logo.product?.sku}&pageSize=25&pageIndex=1`}
                className="max-w-[200px] overflow-x-hidden overflow-ellipsis whitespace-nowrap hover:underline"
              >
                {logo.title}
              </a>
            ) : (
              <span className="max-w-[200px] overflow-x-hidden overflow-ellipsis whitespace-nowrap">
                {logo.title}
              </span>
            )}
          </div>
        </StrucCol>
      </StrucRow>
      <StrucRow>
        <StrucCol size={3.5} className={"bg-white p-3 items-center"}>
          <div
            className={`h-full flex justify-center items-center w-fit ${isDeleted ? "opacity-50" : ""}`}
          >
            <LogoImage
              logo={logo}
              useHighRes={false}
              className={
                isDeleted
                  ? "bg-white max-h-[74px]"
                  : "bg-white hover:rounded hover:border hover:p-2 max-h-[74px] origin-left hover:scale-[3.5] hover:ease-out transition-all duration-150 ease-out"
              }
            />
          </div>
        </StrucCol>
        <StrucCol
          size={5}
          className={`pl-2 pt-1 text-xs space-y-1 ${isDeleted ? "opacity-60" : ""}`}
        >
          {logo.product?.sku && !logo.product.sku.startsWith("UPLOAD_") ? (
            <span>
              <b>{t("item_no")}</b>{" "}
              <a
                href={`/logo?searchTerm=${logo.product?.sku}&pageSize=25&pageIndex=1`}
                className="hover:underline"
              >
                {logo.product.sku}
              </a>
            </span>
          ) : (
            ""
          )}
          <div className="flex flex-row gap-x-1 capitalize">
            <b>{t("type")}</b>
            {logo.transferMethod.type.toLowerCase() === "direk tryk" ||
            logo.transferMethod.type.toLowerCase() === "screen print"
              ? "Screen Print"
              : logo.transferMethod.type.toLowerCase() === "broderi" ||
                  logo.transferMethod.type.toLowerCase() === "embroidery"
                ? "Embroidery"
                : logo.transferMethod.type.toLowerCase()}
          </div>
          {logo.transferMethod.type.toLowerCase() !== "direk tryk" &&
            logo.transferMethod.type.toLowerCase() !== "screen print" &&
            logo.transferMethod.type.toLowerCase() !== "broderi" &&
            logo.transferMethod.type.toLowerCase() !== "embroidery" && (
              <div className="flex flex-row gap-x-1 capitalize">
                <b>{t("designListStyle.method")}</b>
                {getTransferDisplayName(logo).toLowerCase()}
              </div>
            )}
          <div className="flex flex-row gap-x-1">
            <b>{t("designListStyle.size")}</b>
            <LogoDimensions logo={logo} format="WWxYYmm" />
          </div>
          {(logo.transferMethod.type.toLowerCase() === "broderi" ||
            logo.transferMethod.type.toLowerCase() === "embroidery") &&
            (logo.stitches ?? 0) > 0 && (
              <div className="flex flex-row gap-x-1">
                <b>{t("designListStyle.stitches")}</b>
                {(logo.stitches as number).toLocaleString(i18n.language)}
              </div>
            )}
          {isEditable !== false && (
            <span>
              <b>{t("designListStyle.placement")}</b> {designLogo.viewName}
            </span>
          )}
          <DesignLogoStorageLine logo={logo} />
          <div className="flex flex-row gap-x-1 -mb-1">
            <span>
              <b>{t("designListStyle.colors")}</b>
            </span>
            <div>{colors}</div>
          </div>
        </StrucCol>
      </StrucRow>
    </div>
  );
}

function DesignTitleHeader({ design }: { design: Design }) {
  const { t } = useTranslation();
  return (
    <div className={"flex items-center space-x-4"}>
      <BoxHeaderTypography>
        {t("design")} -{" "}
        <span className={"font-medium normal-case"}>
          {design.product?.mul ?? design.title}
        </span>
      </BoxHeaderTypography>
    </div>
  );
}

// Exported for its heating-toggle test only (DesignDetails renders it).
export function DesignPrice({ design }: { design: Design }) {
  const { t } = useTranslation();
  const { showCustomerPrices } = usePriceVisibility();
  const [includeHeating, setIncludeHeating] = useState(false);
  const [includeLogoFees, setIncludeLogoFees] = useState(false);

  // "Prices on their way" (flag productPendingEnabled): never show the old
  // total while a placed logo's product is still owed by the API worker -
  // the badge replaces the price grid. Always false with the flag off.
  if (isDesignPricePending(design)) {
    return (
      <div>
        <p className={"font-bold"}>{t("designListStyle.designPrice")}</p>
        <ProductPendingBadge
          pending={true}
          sinceUtc={designProductPendingSinceUtc(design)}
          className="mt-1"
        />
      </div>
    );
  }

  if (
    design.product?.totalPrices === undefined ||
    design.product?.totalPrices?.length == 0
  ) {
    return (
      <p className={"font-bold"}>{t("designListStyle.noAvailablePrices")}</p>
    );
  }

  const totalPrices = design.product?.totalPrices || [];

  const sortedPrices = getUniqueSortedPrices(totalPrices);
  const midpoint = Math.ceil(sortedPrices.length / 2);
  const firstHalf = sortedPrices.slice(0, midpoint);
  const secondHalf = sortedPrices.slice(midpoint);

  const transferLogoCount = design.designLogos.filter(
    (dl) => dl.logo.transferMethod.type.toLowerCase() === "transfer",
  ).length;

  // Flag heatingToggleHideEmbroideryOnlyEnabled: heating only applies to
  // transfer logos, so on a design whose logos are ALL embroidery (getTypeText
  // = the "Embroidery" the logo card on the right shows) the heating switch
  // changes nothing and is left out. No logos, mixed types or a logo without
  // a type = the row as today. Always false with the flag off.
  const designLogos = design.designLogos ?? [];
  const hideHeatingToggle =
    enviroment.heatingToggleHideEmbroideryOnlyEnabled &&
    designLogos.length > 0 &&
    designLogos.every(
      (dl) => !!dl.logo && getTypeText(dl.logo) === "Embroidery",
    );

  const renderPrices = (prices: typeof totalPrices) => {
    return prices.map((p) => {
      const key = crypto.randomUUID();
      let priceToRender = { ...p };

      if (includeHeating && design.heating?.prices) {
        const heatingPrice = getProductPrice(
          design.heating.prices,
          p.minQuantity,
        );
        if (heatingPrice) {
          priceToRender.adjustedPrice +=
            heatingPrice.adjustedPrice * transferLogoCount;
          if (
            priceToRender.adjustedCustomerPrice !== undefined &&
            heatingPrice.adjustedCustomerPrice !== undefined
          ) {
            priceToRender.adjustedCustomerPrice +=
              heatingPrice.adjustedCustomerPrice * transferLogoCount;
          }
        }
      }

      if (includeLogoFees) {
        const quantity = p.minQuantity || 1;
        let totalFeePrice = 0;
        let totalFeeCustomerPrice = 0;

        const logoQuantities = new Map<
          number,
          { logo: BaseLogo; totalQty: number }
        >();
        design.designLogos.forEach((dl) => {
          if (dl.logo) {
            const current = logoQuantities.get(dl.logo.id);
            if (current) current.totalQty += quantity;
            else
              logoQuantities.set(dl.logo.id, {
                logo: dl.logo,
                totalQty: quantity,
              });
          }
        });

        logoQuantities.forEach(({ logo, totalQty }) => {
          const fees = logo.product?.additionalFees ?? [];
          fees.forEach((fee) => {
            const isStartup = ["startup", "start-up", "opstart", "film"].some(
              (k) => fee.name.toLowerCase().includes(k),
            );
            const feeQty = isStartup
              ? logo.colors?.length || 1
              : (fee.quantity ?? 1);

            let unitPrice = fee.unitPrice ?? 0;
            let customerUnitPrice = fee.unitPrice ?? 0;

            if (fee.prices && fee.prices.length > 0) {
              const priceObj = getProductPrice(fee.prices, totalQty);
              if (priceObj) {
                unitPrice = priceObj.adjustedPrice;
                customerUnitPrice =
                  priceObj.adjustedCustomerPrice !== undefined &&
                  priceObj.adjustedCustomerPrice !== null
                    ? priceObj.adjustedCustomerPrice
                    : priceObj.adjustedPrice;
              }
            }

            totalFeePrice += unitPrice * feeQty;
            totalFeeCustomerPrice += customerUnitPrice * feeQty;
          });
        });

        priceToRender.adjustedPrice += totalFeePrice / quantity;
        if (priceToRender.adjustedCustomerPrice !== undefined) {
          priceToRender.adjustedCustomerPrice +=
            totalFeeCustomerPrice / quantity;
        }
      }

      return (
        <span key={key} className="text-xs capitalize inline">
          <PriceLine price={priceToRender} />
        </span>
      );
    });
  };

  return (
    <div>
      <p className={"font-bold"}>
        {t("designListStyle.designPrice")}
        <SalesPriceBadge className="ml-1.5" />
      </p>
      <div className={"grid grid-cols-2 gap-0 pt-1"}>
        <div className="flex flex-col gap-y-1">{renderPrices(firstHalf)}</div>
        <div className="flex flex-col gap-y-1">{renderPrices(secondHalf)}</div>
      </div>
      {design.heating && !hideHeatingToggle && (
        <div className="flex items-center space-x-2 mt-2">
          <Switch
            checked={includeHeating}
            onCheckedChange={setIncludeHeating}
          />
          <span className="text-xs">
            {t("designListStyle.previewPricesWithHeatingFees")}
          </span>
        </div>
      )}
      <div className="flex items-center space-x-2 mt-2">
        <Switch
          checked={includeLogoFees}
          onCheckedChange={setIncludeLogoFees}
        />
        <span className="text-xs">
          {t("designListStyle.previewPricesWithLogoFees")}
        </span>
      </div>
    </div>
  );
}

// "Note on design" as a POST-IT (flag designNoteEnabled — UI redesign of the
// 18/8 inline section, drawing by Nichlas 19/8): a small hand-drawn sticky note
// (his own SVG, PostItNoteIcon) pinned in the popup's top-right corner. When a
// note exists its first ~20 characters are written on the mini note; clicking
// opens the full-size note as a popover styled as the same post-it, with the
// textarea directly on the paper, the save flow, and the "never leaves this
// page" disclaimer written at the bottom of the note. The save handler is the
// 18/8 one moved unchanged: PUT via the repository (which patches the detail
// cache optimistically), double-submit guard via `saving`, and 404 (API flag
// DesignNoteEnabled off / older API) hides the post-it instead of erroring.
// Everyone with access to the design may write — the customers are the point.
// Exported for its Clarity-masking test only (the modal itself renders it).
export function DesignNotePostIt({ design }: { design: Design }) {
  const { t } = useTranslation();
  const designRepository = useDesignRepository();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [draft, setDraft] = useState(design.customerNote ?? "");
  const [savedNote, setSavedNote] = useState(design.customerNote ?? "");
  const [updatedAt, setUpdatedAt] = useState<Date | undefined>(
    design.customerNoteUpdatedAtUtc,
  );
  const [updatedBy, setUpdatedBy] = useState<string | undefined>(
    design.customerNoteUpdatedByName,
  );
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  if (!enviroment.designNoteEnabled || unavailable) return null;

  async function handleSave() {
    const trimmed = draft.trim();
    setSaving(true);
    try {
      // Same org id the rename call uses (design.customerId = the design's own
      // organization); empty string clears the note (API stores null).
      const res = await designRepository.updateDesignNote(
        design.customerId,
        design.id,
        trimmed === "" ? null : trimmed,
      );
      setDraft(res.customerNote ?? "");
      setSavedNote(res.customerNote ?? "");
      setUpdatedAt(
        res.customerNoteUpdatedAtUtc
          ? new Date(res.customerNoteUpdatedAtUtc)
          : undefined,
      );
      setUpdatedBy(res.customerNoteUpdatedByName ?? undefined);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1800);
    } catch (e) {
      if (e instanceof DataError && e.errorStatus?.code === 404) {
        // API flag off (or an older API): show nothing instead of an error.
        setUnavailable(true);
      } else {
        useErrorToast(t("designNote.saveFailed"));
      }
    } finally {
      setSaving(false);
    }
  }

  // First ~20 characters written on the mini note so it is visible at a glance
  // that there IS a message; a blank note stays blank.
  const preview = savedNote
    ? savedNote.slice(0, 20) + (savedNote.length > 20 ? "…" : "")
    : "";

  return (
    <>
      {/* The mini post-it button: sits in the header band, just below the
          modal's close-X and above the "Logos in Design" heading. The slight
          tilt makes it read as a stuck-on note; hover straightens/grows it
          (transition disabled under prefers-reduced-motion). */}
      <button
        type="button"
        aria-label={t("designNote.title")}
        title={t("designNote.title")}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className="absolute -top-12 right-0 z-40 h-11 w-10 -rotate-[4deg] drop-shadow-md transition-transform duration-150 hover:rotate-0 hover:scale-110 motion-reduce:transition-none"
      >
        <PostItNoteIcon
          className="h-full w-full"
          aria-hidden="true"
          focusable="false"
        />
        {/* Clarity: the preview is the customer's free text (design 5.2,
            Liste A) -> masked; the textarea below is an input (masked in
            every mode by Clarity itself). */}
        {preview && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-[4px] right-[5px] top-[9px] bottom-[6px] overflow-hidden break-words text-left text-[6px] italic leading-[7px] text-neutral-800"
            {...CLARITY_MASK}
          >
            {preview}
          </span>
        )}
      </button>
      {/* The open note: Nichlas' drawing full size as the frame, with the
          content laid out inside its yellow paper area (the inset skirts the
          pin at the top and the curled corner bottom-right). MUI Popover
          gives click-outside + Escape closing out of the box. */}
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        <div className="relative h-[350px] w-[320px] drop-shadow-xl">
          <PostItNoteIcon
            className="absolute inset-0 h-full w-full"
            aria-hidden="true"
            focusable="false"
          />
          <div className="absolute bottom-[60px] left-[28px] right-[40px] top-[64px] flex flex-col">
            <textarea
              className="w-full flex-1 resize-none border-0 bg-transparent p-0 text-[13px] leading-5 text-neutral-900 outline-none placeholder:text-neutral-500"
              maxLength={500}
              autoFocus
              placeholder={t("designNote.placeholder")}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="mt-2 flex items-center gap-x-2">
              <SuccessButton
                onClick={handleSave}
                disabled={saving}
                className="!h-7 !w-auto !px-3 !py-0 !text-xs"
              >
                {t("designNote.save")}
              </SuccessButton>
              {justSaved && (
                <span className="text-xs font-semibold text-green-700">
                  {t("designNote.saved")}
                </span>
              )}
            </div>
            {/* "Changed <date> by <name>": a user's name -> masked. */}
            {savedNote && updatedAt && updatedBy && (
              <span className="mt-1 text-[10px] text-neutral-600" {...CLARITY_MASK}>
                {t("designNote.changed", {
                  date: formatDate(updatedAt),
                  name: updatedBy,
                })}
              </span>
            )}
            {/* Disclaimer written on the note itself (Nichlas' explicit
                requirement): the note never leaves this page. Extra right
                padding keeps it clear of the curled corner. */}
            <p className="mt-2 pr-12 text-[10px] italic leading-tight text-neutral-600">
              {t("designNote.hint")}
            </p>
          </div>
        </div>
      </Popover>
    </>
  );
}

function DesignInfo({
  design,
  onTitleChange,
}: {
  design: Design;
  onTitleChange: (title: string) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(design?.title ?? "");
  const [editable, setEditable] = useState(false);

  const toggle = () => {
    if (editable) onTitleChange(title);
    setEditable(!editable);
  };

  return (
    <div className={"space-y-2"}>
      <p className="editable-title-field">
        <b className="whitespace-nowrap">{t("name")}</b>{" "}
        {editable ? (
          <input
            className="editable-title-field__input"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                toggle();
              }
            }}
          />
        ) : (
          <span className="editable-title-field__label">{design.title}</span>
        )}
        {editable ? (
          <FontAwesomeIcon
            icon={faCheck}
            onClick={toggle}
            className="cursor-pointer hover:opacity-50"
          />
        ) : (
          <EditIcon
            onClick={toggle}
            className="cursor-pointer hover:opacity-50"
          />
        )}
      </p>
      <p>
        <b>{t("manufacturer")}</b>{" "}
        {design.style?.manufacturer?.name || design.navManufacturerName}
      </p>
      <p>
        <b>{t("model")}</b> {design.style?.model || design.navStyleName}
      </p>
      {design.customer?.type !== "vendor" && (
        <p>
          <b>{t("customer")}</b> {design.customer.name}
        </p>
      )}
      <p>
        <b>{t("designListStyle.creationDate")}</b>{" "}
        {formatDate(design.createdAtUtc)}
      </p>
      {design.note && (
        <div className="mt-2 rounded border border-yellow-300 bg-yellow-50 p-2">
          <b className="text-yellow-800">{t("designListStyle.note")}</b>
          <p className="text-sm text-yellow-800 whitespace-pre-wrap">
            {design.note.replace(/\\r\\n|\\n|\\r/g, "\n")}
          </p>
        </div>
      )}
    </div>
  );
}

const ActionButtons = {
  Cancel: ({ onClick }: DefaultActionButtonProps) => {
    const { t } = useTranslation();
    return (
      <SecondaryButton onClick={onClick} className="w-full">
        {t("designListStyle.cancelUpper")}
      </SecondaryButton>
    );
  },
  Delete: ({
    onClick,
    designState,
  }: DefaultActionButtonProps & { designState?: DesignState }) => {
    const { t } = useTranslation();
    const { role } = useProfileContext();

    // Customer (EndUser) should not see delete button except for drafts
    if (role === Role.EndUser && designState !== DesignState.Draft) {
      return null;
    }

    return (
      <ConfirmationGuard
        anchor={
          <IconButton
            variant="error"
            icon={<TrashcanCrossIcon />}
            className="w-full !h-9"
          >
            {t("designListStyle.deleteDesign")}
          </IconButton>
        }
        text={t("designListStyle.confirmDeleteDesign")}
        onAccept={onClick}
      />
    );
  },
  Approve: ({ onClick, disabled }: DefaultActionButtonProps) => {
    const { t } = useTranslation();
    return (
      <SuccessButton onClick={onClick} disabled={disabled} className="w-full">
        {t("designListStyle.approveUpper")}
      </SuccessButton>
    );
  },
  Reject: ({
    onClick,
    disabled,
    children,
  }: DefaultActionButtonProps & { children?: React.ReactNode }) => {
    const { t } = useTranslation();
    return (
      <WarnButton onClick={onClick} disabled={disabled} className="w-full">
        {children || t("designListStyle.rejectUpper")}
      </WarnButton>
    );
  },
  Clone: ({ onClick }: DefaultActionButtonProps) => {
    const { t } = useTranslation();
    return (
      <IconButton
        variant="dim"
        key="clone-button"
        icon={<FontAwesomeIcon icon={faClone} />}
        onClick={onClick}
        className="w-full !h-9"
      >
        {t("designListStyle.cloneDesign")}
      </IconButton>
    );
  },
  SendApproval: ({ onClick, disabled }: DefaultActionButtonProps) => {
    const { t } = useTranslation();
    return (
      <IconButton
        variant="dim"
        key="send-approval-button"
        icon={<FontAwesomeIcon icon={faShare} />}
        onClick={onClick}
        disabled={disabled}
        className="w-full !h-9"
      >
        {t("designListStyle.sendDesign")}
      </IconButton>
    );
  },
  DownloadPDF: ({ onClick }: DefaultActionButtonProps) => {
    const { t } = useTranslation();
    return (
      <IconButton
        key={"download-button"}
        icon={<FontAwesomeIcon icon={faDownload} />}
        onClick={onClick}
        className="w-full !h-9"
      >
        {t("designListStyle.downloadPdfUpper")}
      </IconButton>
    );
  },
  Copy: ({ onClick }: DefaultActionButtonProps) => {
    const { t } = useTranslation();
    return (
      <SecondaryButton className="w-full" onClick={onClick}>
        {t("designListStyle.copyDesign")}
      </SecondaryButton>
    );
  },
  Skip: ({ onClick, disabled }: DefaultActionButtonProps) => {
    const { t } = useTranslation();
    return (
      <IconButton
        variant="dim"
        key="skip-approval-button"
        icon={<FontAwesomeIcon icon={faForward} />}
        onClick={onClick}
        disabled={disabled}
        className="w-full !h-9"
      >
        {t("designListStyle.skipApproval")}
      </IconButton>
    );
  },
  Edit: ({ onClick }: DefaultActionButtonProps) => {
    const { t } = useTranslation();
    return (
      <IconButton
        icon={<EditIcon />}
        variant="dim"
        className="w-full !h-9"
        onClick={onClick}
      >
        {t("designListStyle.editDesign")}
      </IconButton>
    );
  },
  AddToOrder: ({
    onClick,
    disabled,
    requiresConfirmation,
  }: DefaultActionButtonProps & { requiresConfirmation?: boolean }) => {
    const { t } = useTranslation();
    const button = (
      <IconButton
        variant="success"
        className={"w-full !h-9"}
        disabled={disabled}
        onClick={requiresConfirmation ? undefined : onClick}
        icon={<AddToCartIcon />}
      >
        {t("designListStyle.addToOrder")}
      </IconButton>
    );

    if (requiresConfirmation) {
      return (
        <ConfirmationGuard
          anchor={button}
          text={t("designListStyle.addToOrderDeletedLogosConfirm")}
          onAccept={onClick}
        />
      );
    }

    return button;
  },
};

interface StatusProps {
  intent: DesignModalIntent;
  design: Design;
  role: Role;
  designState: DesignState;
  rejectOption: RejectOption;
  setRejectOption: (option: RejectOption) => void;
  remark: string;
  setRemark: (value: string) => void;
  quantity: number;
  setQuantity: (value: number) => void;
  onClone: () => void;
  onSendApproval: () => void;
  onDownloadPdf: (design: Design) => void;
  onAssignCustomerClick: () => void;
}
function StatusColumn({
  role,
  intent,
  design,
  designState,
  rejectOption,
  setRejectOption,
  remark,
  setRemark,
  quantity,
  setQuantity,
  onClone,
  onSendApproval,
  onDownloadPdf,
  onAssignCustomerClick,
}: StatusProps) {
  const { t } = useTranslation();
  const approvedByMe = isDesignApprovedByMe(design, role);
  const { profile } = useProfileContext();
  const isCustomer =
    profile?.userOrganizationInformations.role === Role.EndUser;
  const isAdminOrVendor = role && [Role.Admin, Role.Vendor].includes(role);
  const minQuantity = getDesignMinQuantity(design);
  // "Prices on their way" (flag productPendingEnabled): "Send for approval"
  // is locked with a tooltip while a placed logo's product is pending -
  // or (flag cloneInstantEnabled) a placed clone is still preparing.
  const pricePending = isDesignActionLocked(design);
  const lockVariant = designActionLockVariant(design);
  const sendApprovalButton = (
    <ProductPendingLock locked={pricePending} variant={lockVariant}>
      <ActionButtons.SendApproval
        onClick={pricePending ? () => {} : onSendApproval}
        disabled={pricePending || undefined}
      />
    </ProductPendingLock>
  );

  switch (intent) {
    case "view":
      break;
    case "reject":
      return (
        <div className="flex flex-col">
          {!isCustomer && (
            <div>
              <RadioButton
                checked={rejectOption == "delete"}
                label={t("designListStyle.deleteThisProduct")}
                onClick={() => setRejectOption("delete")}
              />
            </div>
          )}
          <div>
            <RadioButton
              checked={rejectOption == "remark"}
              label={t("designListStyle.ownRemark")}
              onClick={() => setRejectOption("remark")}
            />
          </div>
          {/* Flag rejectAndEditEnabled: "Reject and fix it myself" for the
              dealer (never the customer) when the design has a style - the
              field the editor requires (a locked import without a style would
              open a blank editor). The remark is required as for "Own remark". */}
          {canRejectAndEdit(design, isCustomer) && (
            <div>
              <RadioButton
                checked={rejectOption == "editAfterReject"}
                label={t("designListStyle.rejectAndEdit")}
                onClick={() => setRejectOption("editAfterReject")}
              />
              {rejectOption == "editAfterReject" && (
                <p className="text-xs text-gray-500 pl-4">
                  {t("designListStyle.rejectAndEditHint")}
                </p>
              )}
            </div>
          )}
          <CustomTextArea
            placeholder={t("designListStyle.remarkPlaceholder")}
            disabled={rejectOption === "delete"}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </div>
      );
    case "approve":
      return <div />;
  }

  switch (designState) {
    case DesignState.Draft:
      return (
        <div className="h-full flex flex-col justify-between gap-y-5">
          <ActionButtons.DownloadPDF onClick={() => onDownloadPdf(design)} />
        </div>
      );
    case DesignState.Rejected:
      return (
        <VStack>
          <DesignStatusElement design={design} />
          <label className="font-semibold pt-3">
            {t("designListStyle.rejectionReason")}
          </label>
          <CustomTextArea disabled={true} value={remark} />
        </VStack>
      );
    case DesignState.Pending:
    case DesignState.Approval:
      return (
        <div className="h-full flex flex-col justify-between gap-y-5">
          <div>
            <DesignStatusElement design={design} />
          </div>
          <ActionButtons.DownloadPDF onClick={() => onDownloadPdf(design)} />
          <AssignCustomerButton
            design={design}
            role={role}
            onClick={onAssignCustomerClick}
          />
          {!(isDesignApprovedByMe(design, role) && role === Role.EndUser) &&
            sendApprovalButton}
        </div>
      );
    case DesignState.Done:
      return (
        <div className="h-full flex flex-col justify-between gap-y-5">
          <ActionButtons.DownloadPDF onClick={() => onDownloadPdf(design)} />
          {sendApprovalButton}
          {/* Clone is deliberately NOT gated on isEditable: the nightly NAV import
              re-locks every design that has been through NAV (isEditable=false), which
              made Clone invisible on ~95% of approved designs. The API births clones
              editable, so cloning a locked design is fine — only the source stays
              locked (Edit keeps its isEditable gate).

              Show only when the clone will actually land. The NAV import nulls StyleId on
              99.98% of approved designs, and 68% of those point at a garment that has left
              the catalog, so the API resolves the live style from the NAV names (flag
              CloneResolveNavStyleEnabled) and reports the verdict as canClone. When that
              verdict is absent (list stub before the detail fetch resolves, or an older
              API / flag off) canCloneDesign falls back to the previous style/navStyleName
              gate, so nothing that used to be cloneable disappears. */}
          {canCloneDesign(design) && (
            <ActionButtons.Clone onClick={onClone} />
          )}
          <AssignCustomerButton
            design={design}
            role={role}
            onClick={onAssignCustomerClick}
          />
          <Stepper
            min={1}
            value={quantity}
            onValueChange={setQuantity}
            className="w-full h-9"
          />
        </div>
      );
  }
}
